/**
 * Superpeak – kurv-UI.
 *
 * Styrer kurv-ikonet i headeren, mini-kurven (slide-over) og
 * ordreoversigten på bestil-siden. Al tilstand kommer fra cart.js.
 */

import {
	addAddon,
	cartSummaryText,
	clearCart,
	formatPrice,
	getCart,
	getItemCount,
	getTotal,
	isEmpty,
	removeAddon,
	removePlan,
	setPlan,
	updateAddonQty,
} from './cart.js';

// Skal matche data-attributterne på tillægskortenes "Tilføj"-knapper i
// pricing-plans.php, så upsell-boksen i mini-kurven kan tilføje den
// samme vare, selvom kunden ikke har set knappen på siden.
const UPSELL_ADDONS = [
	{
		id: 'ekstra-haender',
		name: 'Ekstra hænder',
		price: 2995,
		allowQty: false,
		unitLabel: '',
		blurb: 'fast plads i kalenderen',
	},
	{
		id: 'dns-hosting',
		name: 'DNS Hosting',
		price: 49,
		allowQty: true,
		unitLabel: 'pr. domæne',
		blurb: 'til dine andre domæner',
	},
];

let panelEl = null;
let overlayEl = null;

function escapeHtml( str ) {
	const div = document.createElement( 'div' );
	div.textContent = str == null ? '' : String( str );
	return div.innerHTML;
}

/* -------------------------------------------------------------------- */
/* Panel + overlay (mini-kurv)                                          */
/* -------------------------------------------------------------------- */

function buildPanelShell() {
	overlayEl = document.createElement( 'div' );
	overlayEl.id = 'cart-overlay';
	overlayEl.className =
		'fixed inset-0 z-[65] bg-brand/40 opacity-0 pointer-events-none transition-opacity duration-300';

	panelEl = document.createElement( 'div' );
	panelEl.id = 'cart-panel';
	panelEl.className =
		'fixed inset-y-0 right-0 z-[70] flex w-full max-w-sm translate-x-full flex-col bg-white shadow-[0_0_60px_rgba(11,26,51,.25)] transition-transform duration-300';
	panelEl.setAttribute( 'role', 'dialog' );
	panelEl.setAttribute( 'aria-label', 'Kurv' );

	document.body.appendChild( overlayEl );
	document.body.appendChild( panelEl );

	overlayEl.addEventListener( 'click', closePanel );
}

function openPanel() {
	if ( ! panelEl ) {
		return;
	}
	overlayEl.classList.remove( 'pointer-events-none', 'opacity-0' );
	panelEl.classList.remove( 'translate-x-full' );
	document.documentElement.classList.add( 'overflow-hidden' );
}

function closePanel() {
	if ( ! panelEl ) {
		return;
	}
	overlayEl.classList.add( 'opacity-0' );
	setTimeout( () => overlayEl.classList.add( 'pointer-events-none' ), 300 );
	panelEl.classList.add( 'translate-x-full' );
	document.documentElement.classList.remove( 'overflow-hidden' );
}

/* -------------------------------------------------------------------- */
/* Rendering                                                             */
/* -------------------------------------------------------------------- */

function renderRows( cart, { editable } ) {
	const rows = [];

	if ( cart.plan ) {
		rows.push( `
			<div class="flex items-start justify-between gap-3 border-b border-line py-4">
				<div>
					<div class="font-semibold text-brand">${ escapeHtml( cart.plan.name ) }</div>
					<div class="text-sm text-muted">${ formatPrice( cart.plan.price ) }</div>
				</div>
				${ editable ? '<button type="button" class="text-sm font-medium text-muted hover:text-brand" data-cart-remove-plan>Fjern</button>' : '' }
			</div>
		` );
	}

	cart.addons.forEach( ( addon ) => {
		rows.push( `
			<div class="flex items-start justify-between gap-3 border-b border-line py-4">
				<div>
					<div class="font-semibold text-brand">${ escapeHtml( addon.name ) }</div>
					<div class="text-sm text-muted">${ formatPrice( addon.price ) }${ addon.allowQty ? ' ' + ( addon.unitLabel || 'pr. stk.' ) : '' }</div>
					${
						editable && addon.allowQty
							? `
						<div class="mt-2 flex items-center gap-2">
							<button type="button" class="flex h-7 w-7 items-center justify-center rounded-full border border-line text-brand hover:bg-mist" data-cart-qty-minus data-cart-id="${ addon.id }" aria-label="Færre">−</button>
							<span class="w-6 text-center text-sm font-semibold">${ addon.qty }</span>
							<button type="button" class="flex h-7 w-7 items-center justify-center rounded-full border border-line text-brand hover:bg-mist" data-cart-qty-plus data-cart-id="${ addon.id }" aria-label="Flere">+</button>
						</div>
					`
							: ''
					}
				</div>
				${ editable ? `<button type="button" class="text-sm font-medium text-muted hover:text-brand" data-cart-remove-addon data-cart-id="${ addon.id }">Fjern</button>` : '' }
			</div>
		` );
	} );

	return rows.join( '' );
}

function renderUpsell( cart ) {
	if ( ! cart.plan ) {
		return '';
	}

	const missing = UPSELL_ADDONS.filter( ( addon ) => ! cart.addons.some( ( a ) => a.id === addon.id ) );
	if ( missing.length === 0 ) {
		return '';
	}

	return missing
		.map(
			( addon ) => `
		<div class="mt-4 rounded-xl border border-accent/30 bg-accent/5 p-4">
			<div class="text-xs font-bold uppercase tracking-wide text-accent">Andre har også valgt</div>
			<div class="mt-1 font-semibold text-brand">${ addon.name }</div>
			<div class="text-sm text-muted">${ formatPrice( addon.price ) }${ addon.unitLabel ? ' ' + addon.unitLabel : '' } · ${ addon.blurb }</div>
			<button type="button" class="btn btn-accent mt-3 w-full" data-cart-add-upsell data-cart-id="${ addon.id }">Tilføj</button>
		</div>
	`
		)
		.join( '' );
}

function renderPanel() {
	if ( ! panelEl ) {
		return;
	}
	const cart = getCart();
	const empty = isEmpty( cart );

	panelEl.innerHTML = `
		<div class="flex items-center justify-between border-b border-line px-6 py-5">
			<h2 class="text-lg font-bold text-brand">Din kurv</h2>
			<button type="button" class="text-2xl leading-none text-muted hover:text-brand" data-cart-close aria-label="Luk kurv">&times;</button>
		</div>
		<div class="flex-1 overflow-y-auto px-6">
			${ empty ? '<p class="py-10 text-center text-sm text-muted">Kurven er tom. Vælg en hostingpakke for at komme i gang.</p>' : '' }
			${ renderRows( cart, { editable: true } ) }
			${ renderUpsell( cart ) }
		</div>
		<div class="border-t border-line px-6 py-5">
			<div class="mb-4 flex items-center justify-between text-lg font-bold text-brand">
				<span>I alt</span>
				<span>${ formatPrice( getTotal( cart ) ) }</span>
			</div>
			<a href="/bestil/" class="btn btn-accent w-full ${ empty ? 'pointer-events-none opacity-40' : '' }">Gå til kassen</a>
		</div>
	`;
}

function renderBadges() {
	const count = getItemCount();
	document.querySelectorAll( '[data-cart-badge]' ).forEach( ( el ) => {
		el.textContent = String( count );
		el.style.display = count === 0 ? 'none' : 'flex';
	} );
}

function renderOrderSummary() {
	const el = document.getElementById( 'order-summary' );
	if ( ! el ) {
		return;
	}
	const cart = getCart();
	const empty = isEmpty( cart );

	el.innerHTML = `
		<h2 class="text-lg font-bold text-brand">Din ordre</h2>
		${
			empty
				? '<p class="mt-3 text-sm text-muted">Kurven er tom. <a href="/hosting/" class="font-semibold text-accent">Vælg en hostingpakke</a> for at komme i gang.</p>'
				: `
			<div class="mt-2">${ renderRows( cart, { editable: true } ) }</div>
			${ renderUpsell( cart ) }
			<div class="mt-4 flex items-center justify-between text-lg font-bold text-brand">
				<span>I alt</span>
				<span>${ formatPrice( getTotal( cart ) ) }</span>
			</div>
		`
		}
	`;
}

function renderRequiresPlanButtons() {
	const cart = getCart();
	document.querySelectorAll( '[data-cart-add][data-cart-requires-plan="true"]' ).forEach( ( btn ) => {
		const disabled = ! cart.plan;
		btn.disabled = disabled;
		btn.classList.toggle( 'opacity-40', disabled );
		btn.classList.toggle( 'cursor-not-allowed', disabled );
		btn.title = disabled ? 'Vælg en hostingpakke først' : '';
	} );
}

/**
 * Bestil-siden har et fritekstfelt til domæner. Har kunden DNS Hosting i
 * kurven, viser vi et lille praj om, hvor mange domæner der skal skrives
 * ind (ét pr. linje), så antallet matcher det antal de har valgt.
 */
function renderDomainsHint() {
	const hint = document.getElementById( 'domains-hint' );
	const field = document.getElementById( 'domains-field' );
	if ( ! hint || ! field ) {
		return;
	}

	const dnsAddon = getCart().addons.find( ( a ) => a.id === 'dns-hosting' );
	const qty = dnsAddon ? dnsAddon.qty : 0;

	if ( qty > 0 ) {
		hint.textContent = `Du har valgt DNS Hosting til ${ qty } domæne${ qty > 1 ? 'r' : '' }. Skriv dem herunder, ét domæne pr. linje.`;
		hint.classList.remove( 'hidden' );
		field.placeholder = Array.from( { length: qty }, ( _, i ) => `domæne${ qty > 1 ? i + 1 : '' }.dk` ).join( '\n' );
	} else {
		hint.textContent = '';
		hint.classList.add( 'hidden' );
		field.placeholder = 'dinvirksomhed.dk';
	}
}

function renderAll() {
	renderBadges();
	renderPanel();
	renderOrderSummary();
	renderRequiresPlanButtons();
	renderDomainsHint();
}

/* -------------------------------------------------------------------- */
/* Event handling                                                        */
/* -------------------------------------------------------------------- */

function handleAddClick( btn ) {
	const type = btn.dataset.cartType;
	const id = btn.dataset.cartId;
	const name = btn.dataset.cartName;
	const price = parseInt( btn.dataset.cartPrice, 10 ) || 0;
	const allowQty = btn.dataset.cartQty === 'true';
	const unitLabel = btn.dataset.cartUnit || '';

	if ( type === 'plan' ) {
		setPlan( { id, name, price } );
	} else if ( type === 'addon' ) {
		if ( btn.dataset.cartRequiresPlan === 'true' && ! getCart().plan ) {
			return;
		}
		addAddon( { id, name, price, allowQty, unitLabel } );
	}

	openPanel();
}

function bindEvents() {
	document.addEventListener( 'click', ( e ) => {
		const addBtn = e.target.closest( '[data-cart-add]' );
		if ( addBtn ) {
			handleAddClick( addBtn );
			return;
		}

		if ( e.target.closest( '#cart-toggle' ) ) {
			openPanel();
			return;
		}

		if ( e.target.closest( '[data-cart-close]' ) ) {
			closePanel();
			return;
		}

		if ( e.target.closest( '[data-cart-remove-plan]' ) ) {
			removePlan();
			return;
		}

		const removeAddonBtn = e.target.closest( '[data-cart-remove-addon]' );
		if ( removeAddonBtn ) {
			removeAddon( removeAddonBtn.dataset.cartId );
			return;
		}

		const qtyMinusBtn = e.target.closest( '[data-cart-qty-minus]' );
		if ( qtyMinusBtn ) {
			const cart = getCart();
			const item = cart.addons.find( ( a ) => a.id === qtyMinusBtn.dataset.cartId );
			if ( item ) {
				updateAddonQty( item.id, item.qty - 1 );
			}
			return;
		}

		const qtyPlusBtn = e.target.closest( '[data-cart-qty-plus]' );
		if ( qtyPlusBtn ) {
			const cart = getCart();
			const item = cart.addons.find( ( a ) => a.id === qtyPlusBtn.dataset.cartId );
			if ( item ) {
				updateAddonQty( item.id, item.qty + 1 );
			}
			return;
		}

		const upsellBtn = e.target.closest( '[data-cart-add-upsell]' );
		if ( upsellBtn ) {
			const addon = UPSELL_ADDONS.find( ( a ) => a.id === upsellBtn.dataset.cartId );
			if ( addon ) {
				addAddon( addon );
			}
		}
	} );

	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' ) {
			closePanel();
		}
	} );

	window.addEventListener( 'superpeak:cart-updated', renderAll );
}

/* -------------------------------------------------------------------- */
/* Bestil-siden: fyld det skjulte felt ved afsendelse                    */
/* -------------------------------------------------------------------- */

function bindOrderForm() {
	const form = document.getElementById( 'order-form' );
	if ( ! form ) {
		return;
	}

	const field = document.getElementById( 'cart-summary-field' );
	if ( field ) {
		field.value = cartSummaryText();
		form.addEventListener( 'submit', () => {
			field.value = cartSummaryText();
		} );
	}
}

/**
 * Efter en gennemført bestilling lander kunden på tak-siden, som markerer
 * sig med data-clear-cart. Der er kurven ikke relevant længere.
 */
function clearCartOnThankYou() {
	if ( document.querySelector( '[data-clear-cart]' ) ) {
		clearCart();
	}
}

/* -------------------------------------------------------------------- */
/* Init                                                                   */
/* -------------------------------------------------------------------- */

function init() {
	buildPanelShell();
	bindEvents();
	bindOrderForm();
	clearCartOnThankYou();
	renderAll();
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
